import React, { useEffect, useState } from 'react';
import { useHistory, Link, useLocation } from 'react-router-dom';
import qs from 'qs';
import toast from '~~elements/Toast';
import Button from '~~elements/Button';
import { useQuery } from '~~utils/CommonUtils';
import { SeasonRegistryResource } from '~~apis/resource';
import RMultilingual from './Multilingual';
import { useRegistryDispatch, useRegistryState } from './useRegistry';
import { StyledBackIcon } from '../styled';
import { useTournamentState } from '../useTournament';
import { StyledDiv } from '../../Settings/Styles';
import OverviewSection from './components/OverviewSection';
import BasicSection from './components/BasicSection';
import RequirementSection from './components/RequirementSection';
import DocumentSection from './components/DocumentSection';
import FeeSection from './components/FeeSection';
import ReviewSection from './components/ReviewSection';
import {
  validBasicData,
  updateBasicData,
  validRequirementData,
  updateRequirementData,
  validFeeData,
  updateFeeData,
} from './service';

const Registry = ({ organizer, registryInfo: originRegistryInfo }) => {
  const query = useQuery();
  const urlLocation = useLocation();
  const history = useHistory();
  const part = query.get('part') ? query.get('part') : 'overview';

  const { registryInfo } = useRegistryState();
  const registryDispatch = useRegistryDispatch();
  const { tournamentInfo } = useTournamentState();

  const [editData, setEditData] = useState(null);
  const [uploadPendingFile, setUploadPendingFile] = useState(null);
  const [editApiInProgress, setEditApiInProgress] = useState(false);

  // 沒有買擴充套件就直接ㄅ
  useEffect(() => {
    if (!tournamentInfo.uniqid) {
      return;
    }
    if (!tournamentInfo.config.open_registration) {
      history.push(`/organizer/${organizer.uniqid}/tournament/${tournamentInfo.uniqid}/basic`);
    }
  }, [history, organizer, tournamentInfo]);

  useEffect(() => {
    registryDispatch({ type: 'SET_REGISTRY_INFO', payload: originRegistryInfo });
  }, [originRegistryInfo, registryDispatch]);

  useEffect(() => {
    setEditData(null);
    setUploadPendingFile(null);
  }, [part]);

  const cancelEditing = () => {
    const currentSearchValue = qs.parse(urlLocation.search, { ignoreQueryPrefix: true }) || {};
    currentSearchValue.part = 'overview';

    history.push(`${urlLocation.pathname}?${qs.stringify(currentSearchValue)}`);
  };

  const handleFileUpload = () => {
    return new Promise((resolve, reject) => {
      if (!uploadPendingFile) {
        resolve({});
        return;
      }

      switch (part) {
        case 'basic':
          SeasonRegistryResource.updateRegulationFile({
            seasonRegistryUniqid: registryInfo.uniqid,
            file: uploadPendingFile,
          })
            .then(({ data: newRegistryInfo }) => {
              resolve({ newRegistryInfo });
            })
            .catch((e) => {
              reject(e);
            });
          break;
        default:
          resolve({});
          return;
      }
    });
  };

  const handleEditSubmit = () => {
    if (!editData && !uploadPendingFile) {
      return;
    }

    let targetMethod = null;
    let targetData = null;
    let afterSuccess = () => {};
    if (editData) {
      switch (part) {
        case 'basic': {
          const { error, message, data: validData } = validBasicData(editData);
          if (error) {
            toast(message, { status: 'error' });
            return;
          }
          targetMethod = updateBasicData;
          targetData = validData;
          afterSuccess = (newRegistryInfo) => {
            registryDispatch({
              type: 'SET_REGISTRY_INFO',
              payload: newRegistryInfo,
            });
          };
          break;
        }
        case 'requirement': {
          const { error, message, data: validData } = validRequirementData(editData);
          if (error) {
            toast(message, { status: 'error' });
            return;
          }
          targetMethod = updateRequirementData;
          targetData = validData;
          afterSuccess = (newRegistryInfo) => {
            registryDispatch({
              type: 'SET_REGISTRY_INFO',
              payload: newRegistryInfo,
            });
          };
          break;
        }
        case 'fee': {
          const { error, message, data: validData } = validFeeData(editData);
          if (error) {
            toast(message, { status: 'error' });
            return;
          }
          targetMethod = updateFeeData;
          targetData = validData;
          afterSuccess = (newRegistryInfo) => {
            registryDispatch({
              type: 'SET_REGISTRY_INFO',
              payload: newRegistryInfo,
            });
          };
          break;
        }
        default:
          return;
      }

      if (!targetMethod) {
        return;
      }
    }

    setEditApiInProgress(true);
    handleFileUpload()
      .then((uploadedInfo) => {
        if (!editData) {
          if (uploadedInfo.newRegistryInfo) {
            registryDispatch({
              type: 'SET_REGISTRY_INFO',
              payload: uploadedInfo.newRegistryInfo,
            });
          }
          toast(RMultilingual(`SUCCESS.UPDATE`), { status: 'success' });
          cancelEditing();
          setEditApiInProgress(false);
          return;
        }

        targetMethod(registryInfo.uniqid, targetData)
          .then((responseData) => {
            toast(RMultilingual(`SUCCESS.UPDATE`), { status: 'success' });
            afterSuccess(responseData);
            cancelEditing();
          })
          .catch(() => {
            toast(RMultilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
          })
          .finally(() => {
            setEditApiInProgress(false);
          });
      })
      .catch((e) => {
        toast(RMultilingual(`COMMON.UNKNOWN_ERROR`), { status: 'error' });
        setEditApiInProgress(false);
      });
  };

  const partLink = (targetPart) => {
    if (editData && part !== targetPart) {
      return (
        <label className={`section-list--item disabled ${targetPart}`}>
          {RMultilingual(`SIDEBAR.${targetPart.toUpperCase()}`)}
        </label>
      );
    }

    return (
      <Link
        className={`section-list--item ${targetPart} ${part === targetPart && 'selected'}`}
        to={(location) => {
          const currentSearchValue = qs.parse(location.search, { ignoreQueryPrefix: true }) || {};
          currentSearchValue.part = targetPart;
          return {
            ...location,
            search: qs.stringify(currentSearchValue),
          };
        }}
      >
        {RMultilingual(`SIDEBAR.${targetPart.toUpperCase()}`)}
      </Link>
    );
  };

  const getSection = () => {
    switch (part) {
      case 'overview':
        return <OverviewSection />;
      case 'basic':
        return (
          <BasicSection onChange={setEditData} fileChange={setUploadPendingFile} apiInProgress={editApiInProgress} />
        );
      case 'requirement':
        return <RequirementSection onChange={setEditData} apiInProgress={editApiInProgress} />;
      case 'documents':
        return <DocumentSection />;
      case 'fee':
        return <FeeSection onChange={setEditData} apiInProgress={editApiInProgress} />;
      case 'review':
        return <ReviewSection />;
      default:
        return <></>;
    }
  };

  return (
    <StyledDiv>
      <div className="greet">
        {organizer.logo_url ? (
          <img className="greet--pic" src={organizer.logo_url} alt="" />
        ) : (
          <div className="greet--pic default"></div>
        )}
        <div className="greet--name">
          {organizer.name}
          {' - '}
          {tournamentInfo.title}
        </div>
      </div>
      <div className="panel">
        <div className="section-list">
          <div className="section-list--title"></div>
          {editData ? (
            <label
              className={`section-list--item ${editApiInProgress && `disabled`}`}
              onClick={editApiInProgress ? () => {} : cancelEditing}
            >
              {RMultilingual(`COMMON.CANCEL`)}
            </label>
          ) : (
            <Link
              className="section-list--item"
              to={() => ({
                pathname: `/organizer/${organizer.uniqid}/tournament/${tournamentInfo.uniqid}/basic`,
              })}
            >
              <StyledBackIcon />
              {RMultilingual(`SIDEBAR.BACK`)}
            </Link>
          )}
          {partLink('overview')}
          {partLink('basic')}
          {partLink('requirement')}
          {partLink('documents')}
          {partLink('fee')}
          {partLink('review')}
        </div>
        <div className={`section  ${['requirement'].includes(part) && 'lg'}`}>
          <div className="section--title">
            {RMultilingual(`SECTION_TITLE`)} - {RMultilingual(`SIDEBAR.${part.toUpperCase()}`)}
          </div>
          {(editData || uploadPendingFile) && (
            <div className="section--subtitle">
              {RMultilingual(`FORM.UNSAVED_CONTENT_EXIST`)}
              <Button width="unset" color="red" onClick={handleEditSubmit} disabled={editApiInProgress}>
                {RMultilingual(`COMMON.UPDATE`)}
              </Button>
            </div>
          )}
          {getSection()}
          {(editData || uploadPendingFile) && (
            <div className="section--footer">
              <Button width="unset" color="red" onClick={handleEditSubmit} disabled={editApiInProgress}>
                {RMultilingual(`COMMON.UPDATE`)}
              </Button>
            </div>
          )}
        </div>
      </div>
    </StyledDiv>
  );
};

export default Registry;
